import React, { useState, useEffect } from 'react';
import { scoresheetService } from '../../../shared/services/scoresheetService';
import { MatchDetails } from '../../../shared/services/matchDetails';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';

// Reusable loading component for consistency
const TabLoader: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-8">
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  </div>
);

interface StatsTabProps {
  matchDetails: MatchDetails;
}

const StatsTab: React.FC<StatsTabProps> = ({ matchDetails }) => {
  // Extract team IDs from match details
  const homeTeamId = matchDetails.home_team.id;
  const awayTeamId = matchDetails.away_team.id;
  
  // State for comparison selections
  type SelectionType = 'team' | 'player';
  interface Selection {
    type: SelectionType;
    id: string; // team_id or composite key (player_id_team_id) for players
    name: string;
    teamId?: string; // Optional teamId for player selections
  }

  const [leftSelection, setLeftSelection] = useState<Selection>({
    type: 'team',
    id: homeTeamId,
    name: matchDetails.home_team.name
  });
  const [rightSelection, setRightSelection] = useState<Selection>({
    type: 'team',
    id: awayTeamId,
    name: matchDetails.away_team.name
  });
  const [leftStats, setLeftStats] = useState<any>(null);
  const [rightStats, setRightStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'relevant' | 'all'>('relevant');
  const [players, setPlayers] = useState<Array<{ player_id: string; full_name: string; kit_number: number | null; team_id: string; team_name: string }>>([]);
  const [dropdownOptions, setDropdownOptions] = useState<Array<{ id: string; name: string }>>([]);

  // Accordion state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    top_stats: true,
    attack: true,
    distribution: true,
    defense: true,
    goalkeeper: true
  });

  // Refresh state
  // Removed refresh functionality

  // Fetch players and build dropdown options
  useEffect(() => {
    if (!matchDetails.id) return;

    const fetchPlayers = async () => {
      try {
        const matchPlayers = await scoresheetService.getMatchLineupPlayers(matchDetails.id);
        setPlayers(matchPlayers);
        
        // Build dropdown options: teams + all players
        const options: Array<{ id: string; name: string }> = [];
        
        // Add home team
        options.push({
          id: `team_${homeTeamId}`,
          name: matchDetails.home_team.name
        });
        
        // Add away team
        options.push({
          id: `team_${awayTeamId}`,
          name: matchDetails.away_team.name
        });
        
        // Add all players from both teams (using composite key: player_id + team_id)
        matchPlayers.forEach(player => {
          options.push({
            id: `player_${player.player_id}_${player.team_id}`, // Composite key: player_id + team_id
            name: `${player.full_name}${player.kit_number ? ` #${player.kit_number}` : ''} (${player.team_name})`
          });
        });
        
        setDropdownOptions(options);
      } catch (err) {
        console.error('Error fetching players for dropdown:', err);
      }
    };

    fetchPlayers();
  }, [matchDetails.id, homeTeamId, awayTeamId, matchDetails.home_team.name, matchDetails.away_team.name]);

  // Fetch stats based on left and right selections
  useEffect(() => {
    if (!matchDetails.id) return;
    
    const isInitialLoad = leftStats === null && rightStats === null;
    
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    
    const fetchData = async () => {
      try {
        // Fetch left stats
        let leftData;
        if (leftSelection.type === 'team') {
          // Optimized: Fetch only this team's stats server-side instead of all match stats
          const teamStatsArray = viewMode === 'relevant' 
            ? await scoresheetService.getTeamStatsForReports(matchDetails.id, leftSelection.id)
            : await scoresheetService.getTeamStats(matchDetails.id, leftSelection.id);
          
          // Transform to expected format (UIMatchStats structure)
          const filteredStats = {
            match_id: matchDetails.id,
            home_team_stats: leftSelection.id === homeTeamId ? teamStatsArray : [],
            away_team_stats: leftSelection.id === awayTeamId ? teamStatsArray : []
          };
          leftData = filteredStats;
        } else {
          // Fetch player stats from player_scoresheet
          // Extract player_id and team_id from composite key
          const playerMatch = leftSelection.id.match(/^player_(.+)_(.+)$/);
          if (playerMatch) {
            const [, playerId, teamId] = playerMatch;
            leftData = viewMode === 'relevant' 
              ? await scoresheetService.getPlayerStatsForReports(matchDetails.id, playerId, teamId)
              : await scoresheetService.getPlayerStats(matchDetails.id, playerId, teamId);
          }
        }
        
        // Fetch right stats
        let rightData;
        if (rightSelection.type === 'team') {
          // Optimized: Fetch only this team's stats server-side instead of all match stats
          const teamStatsArray = viewMode === 'relevant' 
            ? await scoresheetService.getTeamStatsForReports(matchDetails.id, rightSelection.id)
            : await scoresheetService.getTeamStats(matchDetails.id, rightSelection.id);
          
          // Transform to expected format (UIMatchStats structure)
          const filteredStats = {
            match_id: matchDetails.id,
            home_team_stats: rightSelection.id === homeTeamId ? teamStatsArray : [],
            away_team_stats: rightSelection.id === awayTeamId ? teamStatsArray : []
          };
          rightData = filteredStats;
        } else {
          // Fetch player stats from player_scoresheet
          // Extract player_id and team_id from composite key
          const playerMatch = rightSelection.id.match(/^player_(.+)_(.+)$/);
          if (playerMatch) {
            const [, playerId, teamId] = playerMatch;
            rightData = viewMode === 'relevant' 
              ? await scoresheetService.getPlayerStatsForReports(matchDetails.id, playerId, teamId)
              : await scoresheetService.getPlayerStats(matchDetails.id, playerId, teamId);
          }
        }
        
        setLeftStats(leftData);
        setRightStats(rightData);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stats');
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    };
    
    fetchData();
  }, [matchDetails.id, viewMode, leftSelection.id, leftSelection.type, rightSelection.id, rightSelection.type]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle refresh calculated stats
  // Refresh functionality removed - controlled from score app

  // Helper function to get stat value for left or right selection
  const getStatValue = (selection: Selection, stats: any, statKey: string): number => {
    if (!stats) return 0;
    
    const allStats = [...(stats.home_team_stats || []), ...(stats.away_team_stats || [])];
    const stat = allStats.find((s: any) => 
      s.stat_type && s.stat_type.stat_key === statKey
    );
    return stat?.value || 0;
  };

  // Helper function to get left stat value
  const getLeftStatValue = (statKey: string): number => {
    return getStatValue(leftSelection, leftStats, statKey);
  };

  // Helper function to get right stat value
  const getRightStatValue = (statKey: string): number => {
    return getStatValue(rightSelection, rightStats, statKey);
  };

  // Helper function to format stat value with % sign for percentage stats
  const formatStatValue = (value: number, statKey: string): string => {
    // List of stat keys that are percentages
    const percentageStats = [
      'shots_accuracy_percent',
      'goals_conversion_percent', 
      'passing_accuracy_percent',
      'crossing_accuracy_percent',
      'long_pass_accuracy_percent',
      'short_pass_accuracy_percent',
      'backward_pass_percent',
      'possession_percent',
      'tackles_accuracy_percent'
    ];
    
    if (percentageStats.includes(statKey)) {
      return `${value}%`;
    }
    return value.toString();
  };

  // Stat ordering arrays for each category
  const statOrdering: Record<string, string[]> = {
    top_stats: [
      'possession_percent',
      'total_shots',
      'shots_on_target',
      'assist',
      'dribble_won',
      'aerial_won',
      'offside',
      'corner',
      'total_saves'
    ],
    attack: [
      'total_shots',
      'shots_on_target',
      'shots_off_target',
      'shots_from_inside_the_box',
      'shots_from_outside_the_box',
      'assist',
      'attacks',
      'shots_accuracy_percent',
      'goals_conversion_percent',
      'number_of_crosses',
      'crossing_accuracy_percent'
    ],
    distribution: [
      'possession_percent',
      'passes_attempted',
      'passing_accuracy_percent',
      'short_passes_attempted',
      'long_passes_attempted',
      'backward_passes',
      'chances_created',
      'key_passes'
    ],
    defense: [
      'tackles_attempted',
      'tackles_accuracy_percent',
      'fouls_committed',
      'yellow_card',
      'red_card',
      'balls_recovered'
    ],
    goalkeeper: [
      'total_punches',
      'total_claims',
      'total_saves'
    ]
  };

  // Group stats by category - combine left and right stats
  const statsByCategory = React.useMemo(() => {
    if (!leftStats && !rightStats) return {};
    
    const categories: Record<string, any[]> = {
      top_stats: [],
      attack: [],
      distribution: [],
      defense: [],
      goalkeeper: []
    };
    
    // Combine left and right stats to get all unique stat types
    const allStats: any[] = [];
    if (leftStats) {
      allStats.push(...(leftStats.home_team_stats || []), ...(leftStats.away_team_stats || []));
    }
    if (rightStats) {
      allStats.push(...(rightStats.home_team_stats || []), ...(rightStats.away_team_stats || []));
    }
    
    // Get unique stat types by stat_key
    const uniqueStatsMap = new Map<string, any>();
    allStats.forEach(stat => {
      if (!stat.stat_type) return;
      const statKey = stat.stat_type.stat_key;
      if (!uniqueStatsMap.has(statKey)) {
        uniqueStatsMap.set(statKey, stat.stat_type);
      }
    });
    
    // Build categories from unique stat types
    uniqueStatsMap.forEach((statType) => {
      const category = statType.category;
      
      // Add to Top Stats if marked as top_stats
      if (statType.top_stats === true) {
        categories.top_stats.push({ stat_type: statType });
      }
      
      // Add to regular category
      if (categories[category]) {
        categories[category].push({ stat_type: statType });
      }
    });
    
    return categories;
  }, [leftStats, rightStats]);

  // Loading state
  if (loading) {
    return <TabLoader message="Loading stats data..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="text-red-600 mb-2">Error loading stats</div>
          <div className="text-sm text-gray-500">{error}</div>
        </div>
      </div>
    );
  }

  // Empty state - no stats available
  if ((!leftStats && !rightStats) || Object.keys(statsByCategory).length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-300">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Stats Found</h3>
        <p className="text-gray-500 mb-6">No stats available for this match. Stats will appear here once they are processed.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Filter Strip - Similar to Key Events Strip */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 sm:px-4 py-2 sm:py-3 relative">
        {/* Subtle loading overlay when refreshing */}
        {isRefreshing && (
          <div className="absolute inset-0 bg-white bg-opacity-50 rounded-lg flex items-center justify-center z-10">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <div className="flex items-center justify-between">
          {/* Left side: Label */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            <span className="text-xs sm:text-sm font-medium text-gray-700">
              COMPARE:
            </span>
          </div>

          {/* Center: Two Dropdowns with VS */}
          <div className="flex items-center justify-center space-x-4 flex-1">
            {/* Left Dropdown - Home Team Default */}
            <div className="flex-1 max-w-xs">
              <SearchableDropdown
                value={leftSelection.type === 'team' ? `team_${leftSelection.id}` : leftSelection.id}
                onChange={(value) => {
                  if (value === '') {
                    // Reset to default home team
                    setLeftSelection({
                      type: 'team',
                      id: homeTeamId,
                      name: matchDetails.home_team.name
                    });
                  } else if (value.startsWith('team_')) {
                    const teamId = value.replace('team_', '');
                    const teamName = teamId === homeTeamId ? matchDetails.home_team.name : matchDetails.away_team.name;
                    setLeftSelection({ type: 'team', id: teamId, name: teamName });
                  } else if (value.startsWith('player_')) {
                    // Extract player_id and team_id from composite key
                    const playerMatch = value.match(/^player_(.+)_(.+)$/);
                    if (playerMatch) {
                      const [, playerId, teamId] = playerMatch;
                      const player = players.find(p => p.player_id === playerId && p.team_id === teamId);
                      if (player) {
                        setLeftSelection({ 
                          type: 'player', 
                          id: value, // Store composite key
                          name: player.full_name,
                          teamId: teamId
                        });
                      }
                    }
                  }
                }}
                onClear={() => {
                  // Reset to default home team
                  setLeftSelection({
                    type: 'team',
                    id: homeTeamId,
                    name: matchDetails.home_team.name
                  });
                }}
                showClearIcon={leftSelection.type === 'player'} // Only show clear icon for players
                options={dropdownOptions.filter(opt => {
                  // Left dropdown: only home team + all players
                  if (opt.id === `team_${homeTeamId}`) return true;
                  if (opt.id.startsWith('player_')) return true;
                  return false;
                })}
                placeholder="Select team or player"
                disabled={isRefreshing}
                className="w-full"
              />
            </div>

            {/* VS Separator */}
            <div className="flex-shrink-0 px-2">
              <span className="text-xs sm:text-sm font-medium text-gray-500">VS</span>
            </div>

            {/* Right Dropdown - Away Team Default */}
            <div className="flex-1 max-w-xs">
              <SearchableDropdown
                value={rightSelection.type === 'team' ? `team_${rightSelection.id}` : rightSelection.id}
                onChange={(value) => {
                  if (value === '') {
                    // Reset to default away team
                    setRightSelection({
                      type: 'team',
                      id: awayTeamId,
                      name: matchDetails.away_team.name
                    });
                  } else if (value.startsWith('team_')) {
                    const teamId = value.replace('team_', '');
                    const teamName = teamId === homeTeamId ? matchDetails.home_team.name : matchDetails.away_team.name;
                    setRightSelection({ type: 'team', id: teamId, name: teamName });
                  } else if (value.startsWith('player_')) {
                    // Extract player_id and team_id from composite key
                    const playerMatch = value.match(/^player_(.+)_(.+)$/);
                    if (playerMatch) {
                      const [, playerId, teamId] = playerMatch;
                      const player = players.find(p => p.player_id === playerId && p.team_id === teamId);
                      if (player) {
                        setRightSelection({ 
                          type: 'player', 
                          id: value, // Store composite key
                          name: player.full_name,
                          teamId: teamId
                        });
                      }
                    }
                  }
                }}
                onClear={() => {
                  // Reset to default away team
                  setRightSelection({
                    type: 'team',
                    id: awayTeamId,
                    name: matchDetails.away_team.name
                  });
                }}
                showClearIcon={rightSelection.type === 'player'} // Only show clear icon for players
                options={dropdownOptions.filter(opt => {
                  // Right dropdown: only away team + all players
                  if (opt.id === `team_${awayTeamId}`) return true;
                  if (opt.id.startsWith('player_')) return true;
                  return false;
                })}
                placeholder="Select team or player"
                disabled={isRefreshing}
                className="w-full"
              />
            </div>
          </div>

          {/* Right side: View Mode Toggle */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center bg-white border border-gray-300 rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('relevant')}
                disabled={isRefreshing}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'relevant'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Relevant
              </button>
              <button
                onClick={() => setViewMode('all')}
                disabled={isRefreshing}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Stats Sections */}
      <div className="space-y-2">
        {(['top_stats', 'attack', 'distribution', 'defense', 'goalkeeper'] as const).map((category) => {
          const stats = statsByCategory[category];
          if (!stats || stats.length === 0) return null;
          // Get unique stats for this category
          const uniqueStats = stats.reduce((acc, stat) => {
            const key = stat.stat_type.stat_key;
            if (!acc[key]) {
              acc[key] = {
                stat_type: stat.stat_type,
                leftValue: getLeftStatValue(key),
                rightValue: getRightStatValue(key)
              };
            }
            return acc;
          }, {} as Record<string, any>);

          // Sort stats within category using predefined order
          const sortedStats = Object.values(uniqueStats).sort((a: any, b: any) => {
            const orderA = statOrdering[category]?.indexOf(a.stat_type.stat_key) ?? 999;
            const orderB = statOrdering[category]?.indexOf(b.stat_type.stat_key) ?? 999;
            return orderA - orderB;
          });

          const categoryDisplayNames: Record<string, string> = {
            top_stats: 'Top Stats',
            attack: 'Attack',
            distribution: 'Distribution', 
            defense: 'Defense',
            goalkeeper: 'Goalkeeper',
            duels: 'Duels',
            freekick: 'Free Kicks',
            outside: 'Outside',
            errors: 'Errors',
            game_flow: 'Game Flow'
          };

          const categoryIcons: Record<string, string> = {
            top_stats: '⭐',
            attack: '⚽',
            distribution: '⚡',
            defense: '🛡️',
            goalkeeper: '🥅',
            duels: '🤝',
            freekick: '🎯',
            outside: '🌍',
            errors: '❌',
            game_flow: '⏱️'
          };

          return (
            <div key={category} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
                onClick={() => toggleSection(category)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                    <span className="text-sm">{categoryIcons[category] || '📊'}</span>
            </div>
                  <span className="font-semibold text-gray-900">
                    {categoryDisplayNames[category] || category}
                  </span>
        </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">
                    {sortedStats.length} stats
                  </span>
            <svg 
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      expandedSections[category] ? 'rotate-180' : ''
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
                      </button>
              
              {expandedSections[category] && (
            <div className="border-t border-gray-200 p-4">
                  <div className="space-y-3">
                    {sortedStats.map((stat: any) => {
                      // Determine which selection has the better stat
                      const leftValue = stat.leftValue;
                      const rightValue = stat.rightValue;
                      const leftIsBetter = leftValue > rightValue;
                      const rightIsBetter = rightValue > leftValue;
                      
                      return (
                        <div key={stat.stat_type.stat_key} className="flex items-center justify-between w-full">
                          {/* Left Selection Value - Left Aligned */}
                          <div className={`w-16 text-left relative ${leftIsBetter ? 'bg-blue-50 rounded-md px-2 py-1' : ''}`}>
                            {leftIsBetter && (
                              <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-1 h-4 bg-blue-500 rounded-full"></div>
                            )}
                            <span className="text-lg font-bold text-gray-800">
                              {formatStatValue(leftValue, stat.stat_type.stat_key)}
                            </span>
                          </div>
                          
                          {/* Stat Name - Centered */}
                          <div className="flex-1 text-center px-4">
                            <span className="text-sm font-medium text-gray-700">
                              {stat.stat_type.display_name}
                            </span>
                          </div>
                          
                          {/* Right Selection Value - Right Aligned */}
                          <div className={`w-16 text-right relative ${rightIsBetter ? 'bg-red-50 rounded-md px-2 py-1' : ''}`}>
                            {rightIsBetter && (
                              <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-1 h-4 bg-red-500 rounded-full"></div>
                            )}
                            <span className="text-lg font-bold text-gray-800">
                              {formatStatValue(rightValue, stat.stat_type.stat_key)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
          </div>
      </div>
          )}
        </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatsTab;